import React from "react";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Logout as LogoutIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import AppDialog from "./AppDialog";

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "delete" | "logout" | "warning" | "info";
  loading?: boolean;
  loadingText?: string;
  severity?: "error" | "warning" | "info";
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  type = "warning",
  loading = false,
  loadingText = "Procesando...",
  severity = "warning",
}) => {
  const getIcon = () => {
    switch (type) {
      case "delete":
        return <DeleteIcon color="error" />;
      case "logout":
        return <LogoutIcon color="error" />;
      case "warning":
        return <WarningIcon color="warning" />;
      case "info":
        return <InfoIcon color="info" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getConfirmButtonColor = () => {
    switch (severity) {
      case "error":
        return "error";
      case "warning":
        return "warning";
      case "info":
        return "primary";
      default:
        return "primary";
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirmation-dialog-title" sx={{ flexShrink: 0 }}>
        <Box display="flex" alignItems="center" gap={1}>
          {getIcon()}
          {title}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 0, overflowY: "auto", flex: 1 }}>
        <DialogContentText id="confirmation-dialog-description">
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Button onClick={onClose} color="primary" disabled={loading}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={getConfirmButtonColor()}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : undefined}
        >
          {loading ? loadingText : confirmText}
        </Button>
      </DialogActions>
    </AppDialog>
  );
};

export default ConfirmationDialog;
